<!--
  SignedThumbnailImage — <img> for a document preview whose signed URL is fetched with the
  Bearer token (Spec 111). Renders nothing until the URL is there.

  Slot `fallback`: rendered when the URL cannot be fetched or the image fails to load.
  Events: `load` (image shown), `error` (URL fetch or image load failed).
  On an image load error the remembered URL is dropped (it may have expired), so the next
  display asks the backend again.
-->
<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { api } from '../api';

  export let documentId: string;
  export let size: 'medium' | 'large' = 'medium';
  export let alt = '';
  export let className = '';
  export let lazy = false;

  const dispatch = createEventDispatcher<{ load: void; error: void }>();

  let url = '';
  let current = '';
  let failed = false;

  $: fetchUrl(documentId, size);

  async function fetchUrl(id: string, imageSize: 'medium' | 'large') {
    const key = `${id}|${imageSize}`;
    current = key;
    url = '';
    failed = false;
    try {
      const signed = await api.documents.getThumbnailSignedUrl(id, imageSize);
      if (current === key) url = signed;
    } catch {
      if (current === key) fail();
    }
  }

  function fail() {
    failed = true;
    dispatch('error');
  }

  function handleImageError() {
    api.documents.forgetThumbnailSignedUrl(documentId, size);
    url = '';
    fail();
  }
</script>

{#if url}
  <img
    src={url}
    {alt}
    class={className}
    loading={lazy ? 'lazy' : undefined}
    on:load={() => dispatch('load')}
    on:error={handleImageError}
  />
{:else if failed}
  <slot name="fallback" />
{/if}
